/**
 * Finnhub API Provider
 * 需要API密钥，支持股票、外汇、加密货币
 */

import {
  ApiProvider,
  Asset,
  AssetPrice,
  AssetType,
  TimeRange,
  HistoricalData,
  AssetSearchResult,
  AssetValidationResult,
  RequestConfig
} from '@/types/api';

import { finnhubService } from '../finnhub';
import { createApiError, categorizeHttpError, extractRetryAfter } from '../errors';
import { getApiKey } from '../apiKeyManager';

export class FinnhubProvider implements ApiProvider {
  name = 'finnhub';
  priority = 4;
  isHealthy = true;
  lastError?: Date;
  supportedAssetTypes: AssetType[] = ['crypto', 'stock', 'forex'];
  
  private readonly timeout = 10000;
  
  async getAssetPrice(asset: Asset, config?: RequestConfig): Promise<AssetPrice> {
    try {
      const apiKey = getApiKey('finnhub');
      if (!apiKey) throw createApiError('AUTH_ERROR', 'Finnhub API key is required', { provider: this.name });
      
      finnhubService.setApiKey(apiKey);
      
      let result: AssetPrice;
      switch (asset.type) {
        case 'stock':
          result = await finnhubService.getStockPrice(asset.symbol);
          break;
        case 'forex':
          const [from, to] = this.parseForexSymbol(asset.symbol);
          result = await finnhubService.getForexRate(from, to);
          break;
        case 'crypto':
          const results = await finnhubService.getCryptoPrice([asset.id]);
          if (results.length === 0) throw new Error(`Crypto asset not found: ${asset.symbol}`);
          result = results[0];
          break;
        default:
          throw createApiError('VALIDATION_ERROR', `Unsupported asset type: ${asset.type}`, { provider: this.name });
      }
      return {
        ...result,
        id: asset.id,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }
  
  async getAssetPrices(assets: Asset[], config?: RequestConfig): Promise<AssetPrice[]> {
    const results: AssetPrice[] = [];
    for (const asset of assets) {
      try {
        results.push(await this.getAssetPrice(asset, config));
      } catch (error) {
        console.warn(`Failed to get price for ${asset.symbol}:`, error);
      }
    }
    return results;
  }
  
  async getHistoricalData(asset: Asset, timeRange: TimeRange, config?: RequestConfig): Promise<HistoricalData> {
    throw createApiError('NOT_FOUND_ERROR', 'Historical data not supported by Finnhub', { provider: this.name });
  }
  
  async searchAssets(query: string, type?: AssetType, config?: RequestConfig): Promise<AssetSearchResult[]> {
    return this.getKnownAssets(type).filter(a => 
      a.symbol.toLowerCase().includes(query.toLowerCase()) || 
      a.name.toLowerCase().includes(query.toLowerCase())
    );
  }
  
  async validateAsset(asset: Partial<Asset>, config?: RequestConfig): Promise<AssetValidationResult> {
    if (!asset.symbol || !asset.type) {
      return { valid: false, error: 'Asset symbol and type are required' };
    }
    
    // For symbols with exchange suffix (e.g., 0700.HK, 600519.SH), preserve the format
    const symbol = asset.symbol.toUpperCase();
    const testAsset: Asset = {
      id: asset.id || symbol.toLowerCase().replace(/\./g, '_'),
      symbol: symbol, // Preserve original symbol format including exchange suffix
      name: asset.name || symbol,
      type: asset.type,
      priceStatus: 'loading',
      displayOrder: 0,
      isActive: true,
      ...(asset.exchange && { exchange: asset.exchange })
    };
    
    try {
      const priceData = await this.getAssetPrice(testAsset, { ...config, timeout: 5000 });
      return {
        valid: true,
        asset: {
          ...testAsset,
          price: priceData.current_price,
          ...(priceData.source_url ? { sourceUrl: priceData.source_url } : {}),
          priceStatus: 'success' as const
        }
      };
    } catch (error) {
      return { valid: false, error: `Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}` };
    }
  }
  
  async testConnection(config?: RequestConfig): Promise<boolean> {
    try {
      const apiKey = getApiKey('finnhub');
      if (!apiKey || apiKey.trim() === '') return false;
      
      const testAsset: Asset = { id: 'appl', symbol: 'APPL', name: 'Apple', type: 'stock', priceStatus: 'loading', displayOrder: 0, isActive: true };
      await this.getAssetPrice(testAsset, { ...config, timeout: 5000 });
      return true;
    } catch {
      return false;
    }
  }
  
  private parseForexSymbol(symbol: string): [string, string] {
    if (symbol.includes('/')) {
      const [from, to] = symbol.split('/');
      return [from.trim(), to.trim()];
    }
    if (symbol.includes('_')) {
      const [from, to] = symbol.split('_');
      return [from.trim(), to.trim()];
    }
    if (symbol.length === 6) {
      return [symbol.substring(0, 3), symbol.substring(3)];
    }
    throw new Error(`Invalid forex symbol format: ${symbol}`);
  }
  
  private getKnownAssets(type?: AssetType): AssetSearchResult[] {
    const all: AssetSearchResult[] = [
      { id: 'appl', symbol: 'APPL', name: 'Apple Inc.', type: 'stock' },
      { id: 'msft', symbol: 'MSFT', name: 'Microsoft', type: 'stock' },
      { id: 'usd_eur', symbol: 'USD/EUR', name: 'USD/EUR', type: 'forex' },
      { id: 'bitcoin', symbol: 'BTC', name: 'Bitcoin', type: 'crypto' }
    ];
    return type ? all.filter(a => a.type === type) : all;
  }
  
  private handleError(error: unknown): never {
    if (error instanceof Error && 'code' in error) throw error;
    if (error instanceof Error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw createApiError('NETWORK_ERROR', 'Network connection failed', { provider: this.name, originalError: error });
      }
      if (error.name === 'AbortError' || error.message.includes('timeout')) {
        throw createApiError('TIMEOUT_ERROR', 'Request timed out', { provider: this.name, originalError: error });
      }
      if ('response' in error && error.response) {
        const response = error.response as any;
        const retryAfter = response.headers ? extractRetryAfter(response.headers as Record<string, string>) : undefined;
        throw createApiError(
          categorizeHttpError(response.status, response.data?.message),
          response.data?.message || error.message,
          { provider: this.name, originalError: error, ...(retryAfter ? { retryAfter } : {}) }
        );
      }
      throw createApiError('UNKNOWN_ERROR', error.message, { provider: this.name, originalError: error });
    }
    throw createApiError('UNKNOWN_ERROR', 'An unknown error occurred', { provider: this.name, originalError: error });
  }
}
